import React from 'react'
import { Icons } from '@pankod/refine-antd'
import { useCan, useCreate, useInvalidate } from '@pankod/refine-core'

import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import MenuItemPopconfirm from '../../MenuItemPopconfirm'
import { Highlighter } from '../../utils'
import { StatusTag } from '../StatusTag'

import type {
  TConfigurationMenuItems,
  TConfigurationSectionContext,
} from '../types'

export const useParticipantStatusItems = ({
  hideAddParticipantActive,
  invalidateProgramDetail,
  isOwner,
  program,
  programId,
  search,
}: TConfigurationSectionContext): TConfigurationMenuItems => {
  const invalidate = useInvalidate()
  const { mutateAsync: toggleAutoApprove } = useCreate()
  const { mutateAsync: toggleHideAddParticipant } = useCreate()
  const { data: canToggleAutoApprove } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'toggle-auto-approve-diklat-participants' },
  })
  const { data: canToggleHideAddParticipant } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'toggle-hide-add-participant' },
  })
  const autoApproveActive = program?.autoApproveDiklatParticipantsActive
  const canUpdateAutoApprove = canToggleAutoApprove?.can || isOwner

  return [
    {
      key: 'Auto Approve Peserta Diklat',
      type: 'group',
      label: (
        <div className="flex justify-between gap-2">
          <Highlighter search={search} text="Auto Approve Peserta Diklat" />
          <StatusTag
            active={!!autoApproveActive}
            activeText="AKTIF"
            inactiveText="NONAKTIF"
          />
        </div>
      ),
      children: [
        {
          icon: autoApproveActive ? (
            <Icons.StopOutlined />
          ) : (
            <Icons.SafetyOutlined />
          ),
          key: autoApproveActive
            ? 'Nonaktifkan Auto Approve Peserta Diklat'
            : 'Aktifkan Auto Approve Peserta Diklat',
          danger: autoApproveActive && canUpdateAutoApprove,
          disabled: !canUpdateAutoApprove,
          label: (
            <MenuItemPopconfirm
              title={`Apakah Anda ingin ${
                autoApproveActive ? 'me-nonaktifkan' : 'mengaktifkan'
              } fitur Auto Approve Peserta Diklat?`}
              okText={autoApproveActive ? 'Nonaktifkan' : 'Aktifkan'}
              cancelText="Batal"
              disabled={!canUpdateAutoApprove}
              okButtonProps={{ danger: autoApproveActive }}
              onConfirm={() =>
                toggleAutoApprove(
                  {
                    resource: `programs/${programId}/auto-approve-diklat-participants`,
                    dataProviderName: 'lms',
                    metaData: { httpMethod: 'post' },
                    values: { active: !autoApproveActive },
                    successNotification: {
                      message:
                        'Fitur auto approve peserta diklat berhasil dikonfigurasi ulang',
                      type: 'success',
                      description: 'Sukses',
                    },
                    errorNotification: (error) =>
                      showErrorNotification(
                        error,
                        'Terdapat gangguan saat mengubah konfigurasi auto approve peserta diklat',
                      ),
                  },
                  { onSuccess: invalidateProgramDetail },
                )
              }
            >
              <Highlighter
                search={search}
                text={
                  autoApproveActive
                    ? 'Nonaktifkan Auto Approve Peserta Diklat'
                    : 'Aktifkan Auto Approve Peserta Diklat'
                }
              />
            </MenuItemPopconfirm>
          ),
        },
      ],
    },
    ...(canToggleHideAddParticipant?.can
      ? [
          {
            key: 'Hide Tambah Peserta',
            type: 'group' as const,
            label: (
              <div className="flex justify-between gap-2">
                <Highlighter search={search} text="Hide Tambah Peserta" />
                <StatusTag
                  active={hideAddParticipantActive}
                  activeText="AKTIF"
                  inactiveText="NONAKTIF"
                />
              </div>
            ),
            children: [
              {
                icon: hideAddParticipantActive ? (
                  <Icons.StopOutlined />
                ) : (
                  <Icons.UsergroupAddOutlined />
                ),
                key: hideAddParticipantActive
                  ? 'Nonaktifkan Hide Tambah Peserta'
                  : 'Aktifkan Hide Tambah Peserta',
                danger: hideAddParticipantActive,
                label: (
                  <MenuItemPopconfirm
                    title={`Apakah Anda ingin ${
                      hideAddParticipantActive
                        ? 'me-nonaktifkan'
                        : 'mengaktifkan'
                    } fitur hide tambah peserta?`}
                    okText={
                      hideAddParticipantActive ? 'Nonaktifkan' : 'Aktifkan'
                    }
                    cancelText="Batal"
                    okButtonProps={{ danger: hideAddParticipantActive }}
                    onConfirm={() =>
                      toggleHideAddParticipant(
                        {
                          resource: `programs/${programId}/hide_add_participant/flag`,
                          dataProviderName: 'lms',
                          metaData: { httpMethod: 'put' },
                          values: {
                            isActive: !hideAddParticipantActive,
                          },
                          successNotification: {
                            message:
                              'Fitur hide tambah peserta berhasil dikonfigurasi ulang',
                            type: 'success',
                            description: 'Sukses',
                          },
                          errorNotification: (error) =>
                            showErrorNotification(
                              error,
                              'Terdapat gangguan saat mengubah konfigurasi hide tambah peserta',
                            ),
                        },
                        {
                          onSuccess: () =>
                            invalidate({
                              invalidates: ['detail'],
                              dataProviderName: 'lms',
                              resource: `programs/${programId}/hide_add_participant`,
                              id: 'flag',
                            }),
                        },
                      )
                    }
                  >
                    <Highlighter
                      search={search}
                      text={
                        hideAddParticipantActive
                          ? 'Nonaktifkan Hide Tambah Peserta'
                          : 'Aktifkan Hide Tambah Peserta'
                      }
                    />
                  </MenuItemPopconfirm>
                ),
              },
            ],
          },
        ]
      : []),
  ]
}
